// dev-collection — Marqo-powered Product Listing Page (PLP) controller.
//
// Mounted by sections/dev-collection.liquid as the custom element
// <dev-collection-plp>. It drives the whole collection/search grid off the
// Marqo public API (window.Marqo, published by assets/marqo.js — gated on
// settings.marqo_enabled). This file is a plain theme asset (NOT part of the
// app/scripts esbuild graph), so it cannot `import` the internal plp/* helpers
// (buildQueryState / buildRequestBody / dedupHits / priceSlider). Those are
// replicated here from the *public* primitives only:
//
//   Marqo.collections(body) / Marqo.search(body)  — network (dedup is internal)
//   Marqo.buildFilter(entries)                    — URL params → Marqo filter DSL
//   Marqo.sortByFromActive(field, dir)            — sort dropdown → sortBy body
//   Marqo.DEFAULT_FACETS / Marqo.labelFor(field)  — facet config + labels
//   Marqo.buildCard(hits, cardEl)                 — hits[] → DocumentFragment
//
// The card template contract is verified against app/scripts/common/Marqo/
// productCard/*. buildCard's 2nd arg is the CARD ELEMENT pulled from inside
// the <template> via `.content.querySelector('.marqo-product-card')` — exactly
// how recommendations/element.js does it — NOT the <template> node itself.

(function () {
  'use strict';

  const ELEMENT_NAME = 'dev-collection-plp';

  // ── money formatting ────────────────────────────────────────────────
  // productCard/price.js calls `el.set?.(cents)` on the price slots. The
  // theme has no <money> element, so we ship a tiny <money-set> whose
  // .set(cents) formats per the shop money_format string (passed down on
  // the host as data-money-format) and writes textContent.
  let MONEY_FORMAT = '${{amount}}';
  let MONEY_CURRENCY = 'USD';

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands == null ? ',' : thousands;
    decimal = decimal == null ? '.' : decimal;
    const value = (Number(number) || 0).toFixed(precision);
    const parts = value.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const cents = parts[1] ? decimal + parts[1] : '';
    return dollars + cents;
  }

  function formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    const fmt = MONEY_FORMAT;
    if (!fmt || !/\{\{\s*\w+\s*\}\}/.test(fmt)) {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: MONEY_CURRENCY || 'USD',
        }).format(amount);
      } catch (_) {
        return '$' + formatWithDelimiters(amount, 2);
      }
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_m, name) {
      switch (name) {
        case 'amount': return formatWithDelimiters(amount, 2, ',', '.');
        case 'amount_no_decimals': return formatWithDelimiters(amount, 0, ',', '.');
        case 'amount_with_comma_separator': return formatWithDelimiters(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return formatWithDelimiters(amount, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return formatWithDelimiters(amount, 2, "'", '.');
        case 'amount_no_decimals_with_space_separator': return formatWithDelimiters(amount, 0, ' ', '.');
        case 'amount_with_space_separator': return formatWithDelimiters(amount, 2, ' ', ',');
        default: return formatWithDelimiters(amount, 2, ',', '.');
      }
    });
  }

  if (!customElements.get('money-set')) {
    customElements.define('money-set', class extends HTMLElement {
      set(cents) {
        const n = Number(cents);
        if (!Number.isFinite(n)) return;
        // Guard: a 0 / missing price (e.g. an index without price data) should
        // not render a broken-looking "$0.00" — leave it blank instead.
        if (n <= 0) { this.textContent = ''; this.closest('.dev-plp-card__price')?.classList.add('dev-plp-card__price--unpriced'); return; }
        this.textContent = formatMoney(n);
      }
    });
  }

  // ── helpers ─────────────────────────────────────────────────────────
  const FILTER_PREFIX = 'filter.';
  const PRICE_LOW_KEY = 'filter.price.low';
  const PRICE_HIGH_KEY = 'filter.price.high';
  const FACET_SEARCH_MIN = 10; // a facet group only gets a search box past this many options

  // Facet display order (by Marqo field name) to match the Figma rail:
  // Categories, Departements, Brands, Gender, Special Offers, Colors, Size.
  // Any returned facet not listed here is appended after these, in
  // DEFAULT_FACETS order, so nothing is silently dropped.
  const FACET_ORDER = [
    'productMetafieldCustomLevel1Categories',
    'productType',
    'productVendor',
    'variantMetafieldFiltersWebFiltersGender',
    'variantMetafieldFiltersSpecialOffers',
    'variantMetafieldFiltersWebFiltersColour',
    'variantMetafieldFiltersWebFiltersSize',
  ];

  // attributesToRetrieve whitelist — mirrors app/scripts/common/Marqo/
  // productCard/attributes.js PLP_ATTRIBUTES_TO_RETRIEVE. RD's Marqo docs
  // can be ~1MB each (per-store inventory + long metafields); without this
  // every /search and /collections call pulls full docs. Keep in sync if
  // the card starts consuming new fields.
  const PLP_ATTRIBUTES = [
    '_id', 'parentProductId', 'productTags', 'productTitle', 'productTitle_fr',
    'productVendor', 'productHandle', 'productHandle_fr', 'variantImageUrl', 'productImageUrls',
    'siblingImageUrls', 'siblingIds', 'siblingVariantIds', 'price', 'priceMin',
    'priceMax', 'comparePrice', 'promoPrice', 'promoPriceMin', 'promoPriceMax', 'maxDiscount', 'maxPromoDiscount',
    'mfield_custom_call_for_price', 'productOutOfStock',
    'productMetafieldCustomCurrentInventoryNew', 'productMetafieldCustomFitmentScope',
    'productMetafieldFitmentFitmentScope', 'productMetafieldCustomPlpCardTag',
  ];

  // Read a facet bucket as [label, info] entries. Marqo keys string/array
  // facets by the facet value; defensively unwrap a literal `value` wrapper
  // in case a future shape nests the label map under it.
  function facetEntries(bucket) {
    if (!bucket || typeof bucket !== 'object') return [];
    const keys = Object.keys(bucket);
    if (keys.length === 1 && keys[0] === 'value' && bucket.value
        && typeof bucket.value === 'object' && !('count' in bucket.value)) {
      return Object.entries(bucket.value);
    }
    return Object.entries(bucket);
  }

  function marqo() { return window.Marqo; }

  // Read all `filter.*` entries straight off the URL — buildFilter() accepts
  // an iterable of [key, value] and handles the price/fitment special keys itself.
  function filterEntries(params) {
    return [...params.entries()].filter(([k]) => k.startsWith(FILTER_PREFIX));
  }

  // ── controller ──────────────────────────────────────────────────────
  if (!customElements.get(ELEMENT_NAME)) {
    customElements.define(ELEMENT_NAME, class extends HTMLElement {
      // The theme's <lazy-script> upgrade detaches/re-attaches descendants,
      // firing connectedCallback twice — guard against a double initial load.
      #initialized = false;
      #cardTemplate = null;
      #page = 1;
      #busy = false;
      #reqSeq = 0;          // monotonic token so a stale query can't overwrite a newer one
      #onPopstate = null;   // bound handler, removed on disconnect
      #onResize = null;     // bound, debounced — re-checks swatch nav fit on resize
      #io = null;           // IntersectionObserver for infinite scroll (null when off/unsupported)
      #heroTitleEl = null;  // <h1> hero title, retitled when a vehicle deep link is active
      #heroTitleDefault = ''; // the Liquid-rendered collection title, restored when no vehicle
      #fitmentPrefix = '';  // "Guaranteed To Fit Your" (section setting)
      #heroBoxEl = null;    // hero box, parent for a JS-created copy element
      #heroCopyEl = null;   // hero copy/description (.dev-collection__hero-copy), may not exist
      #heroCopyDefault = null; // original copy innerHTML (null if none was rendered)
      #heroCopyCreated = false; // true when WE created the copy element (remove it on restore)
      #fitmentCopyTpl = ''; // hero copy template, [vehicle] token (section setting)
      #heroImgEl = null;    // <img> inside the hero <picture>; null when no default hero image
      #heroSourceEl = null; // <source media="(max-width: 768px)"> inside the hero <picture>, may not exist
      #heroImgDefaultSrc = '';
      #heroImgDefaultSrcset = '';
      #heroSourceDefaultSrcset = '';
      #sbvBanners = null;   // { [normalized vehicle type]: { desk, mob } } — null when no entries configured

      async connectedCallback() {
        if (this.#initialized) return;
        this.#initialized = true;

        if (!marqo()) {
          console.error(`[${ELEMENT_NAME}] window.Marqo missing — is settings.marqo_enabled on?`);
          return this.#fail();
        }
        const cfg = window.theme && window.theme.marqo;
        if (!cfg || !cfg.endpoint || !cfg.indexName || !cfg.indexId) {
          console.error(`[${ELEMENT_NAME}] missing window.theme.marqo config`, cfg);
          return this.#fail();
        }

        // Money formatting config (passed from Liquid).
        if (this.dataset.moneyFormat) MONEY_FORMAT = this.dataset.moneyFormat;
        if (this.dataset.currency) MONEY_CURRENCY = this.dataset.currency;

        // Fitment hero: when the shopper arrives via a vehicle deep link
        // (filter.fitments=…, from Your Garage / Ride Selector), the hero title
        // becomes "<prefix> <vehicle>" instead of the collection name. Cache the
        // original so removing the vehicle restores it.
        this.#heroTitleEl = this.querySelector('.dev-collection__hero-title');
        this.#heroTitleDefault = this.#heroTitleEl ? this.#heroTitleEl.textContent.trim() : '';
        this.#fitmentPrefix = (this.dataset.fitmentTitlePrefix || 'Guaranteed To Fit Your').trim();
        this.#heroBoxEl = this.querySelector('.dev-collection__hero-box');
        this.#heroCopyEl = this.querySelector('.dev-collection__hero-copy');
        this.#heroCopyDefault = this.#heroCopyEl ? this.#heroCopyEl.innerHTML : null;
        this.#fitmentCopyTpl = (this.dataset.fitmentHeroCopy || '').trim();

        // Shop My Ride banner swap — same deep-link trigger as the title/copy
        // above. Only wired up when a default hero image exists at all (the
        // picture/img this swaps); a collection with no default hero doesn't
        // get one dynamically built from nothing, same posture as the rest of
        // this cache-and-restore pattern.
        const heroPicture = this.querySelector('.dev-collection__hero-media picture');
        this.#heroImgEl = heroPicture ? heroPicture.querySelector('img') : null;
        this.#heroSourceEl = heroPicture ? heroPicture.querySelector('source') : null;
        this.#heroImgDefaultSrc = this.#heroImgEl ? this.#heroImgEl.getAttribute('src') || '' : '';
        this.#heroImgDefaultSrcset = this.#heroImgEl ? this.#heroImgEl.getAttribute('srcset') || '' : '';
        this.#heroSourceDefaultSrcset = this.#heroSourceEl ? this.#heroSourceEl.getAttribute('srcset') || '' : '';
        this.#sbvBanners = this.#parseSbvBanners();

        // The card ELEMENT lives inside the <template> — pull it out the same
        // way recommendations/element.js does. buildCard clones THIS element.
        this.#cardTemplate = this.querySelector('template[data-marqo-card]')
          ?.content?.querySelector('.marqo-product-card') || null;
        if (!this.#cardTemplate) {
          console.error(`[${ELEMENT_NAME}] card template (.marqo-product-card) not found`);
          return this.#fail();
        }

        this.#bindEvents();
        this.#initInfiniteScroll();
        this.#syncControlsFromUrl();
        // Resolve the real hero title + breadcrumb visibility up front, before the
        // (network-dependent) grid query — handleizeYMM is a pure URL parse, so
        // this doesn't need to wait on Marqo's response. Then reveal them: Liquid's
        // inline critical-path script (sections/dev-collection.liquid) already hid
        // both behind .is-ride-pending on a fitment deep link, so the shopper never
        // sees the collection's own title/breadcrumb before this JS ran.
        this.#applyFitmentHeading();
        this.#applyRideResultsChrome();
        this.classList.remove('is-ride-pending');
        await this.#requery({ replace: true, resetPage: true });
        this.#initCarousels(); // fire-and-forget; independent of the grid
      }

      disconnectedCallback() {
        if (this.#onPopstate) window.removeEventListener('popstate', this.#onPopstate);
        if (this.#onResize) window.removeEventListener('resize', this.#onResize);
        if (this.#io) { this.#io.disconnect(); this.#io = null; }
        document.body.style.overflow = '';
      }

      // ── state ─────────────────────────────────────────────────────
      #readState({ pageOverride } = {}) {
        const params = new URLSearchParams(location.search);
        const limit = Number(this.dataset.limit) || 24;
        const page = pageOverride || this.#page || 1;
        const sortSel = this.querySelector('[data-sort]')?.selectedOptions?.[0];
        return {
          isSearch: this.dataset.isSearch === 'true',
          q: params.get('q') || '',
          collectionName: this.dataset.collectionName || '',
          filterEntries: filterEntries(params),
          sortField: sortSel?.dataset.sortField || '',
          sortDir: sortSel?.dataset.sortDir || '',
          limit,
          offset: (page - 1) * limit,
          page,
        };
      }

      // requestBody replica — public primitives only. Never passes
      // profileId/namespace (the client injects locale internally) and omits
      // attributesToRetrieve (the internal whitelist isn't exported; Marqo
      // returns full hits — heavier but correct. Optimise later if needed).
      #buildBody(s) {
        const M = marqo();
        const body = { limit: s.limit, offset: s.offset, facets: M.DEFAULT_FACETS, attributesToRetrieve: PLP_ATTRIBUTES };
        const filter = M.buildFilter(s.filterEntries);
        if (filter) body.filter = filter;
        const sortBy = M.sortByFromActive(s.sortField, s.sortDir);
        if (sortBy) body.sortBy = sortBy; // omitted entirely for "relevance"
        // Search pages query by term; collection pages identify the collection to Marqo
        // by handle via the /collections endpoint's collectionName parameter.
        if (s.isSearch) body.q = s.q || '*';
        else if (s.collectionName) body.collectionName = s.collectionName;
        return body;
      }

      async #runQuery(s) {
        const M = marqo();
        const body = this.#buildBody(s);
        return s.isSearch ? M.search(body) : M.collections(body);
      }

      // ── query lifecycle ───────────────────────────────────────────
      async #requery({ replace = true, resetPage = false } = {}) {
        if (resetPage) this.#page = 1;
        const seq = ++this.#reqSeq;
        this.#setBusy(true);
        let res;
        try {
          res = await this.#runQuery(this.#readState());
        } catch (err) {
          if (seq !== this.#reqSeq) return; // superseded by a newer query
          console.error(`[${ELEMENT_NAME}] query failed`, err);
          this.#setBusy(false);
          return this.#showEmpty(replace);
        }
        if (seq !== this.#reqSeq) return; // a newer query started; drop this stale response
        const rawHits = (res && res.hits) || [];
        this.#renderCards(rawHits, { replace });
        this.#renderFacets(res && res.facets);
        this.#renderSubpills(res && res.facets);
        this.#syncPriceBounds(res && res.facets && res.facets.price);
        this.#updateTotal(this.#displayTotal(res));
        this.#updateLoadMore(res, rawHits.length);
        this.#renderActiveChips();
        this.#applyFitmentHeading();
        this.#applyRideResultsChrome();
        this.#setBusy(false);
        // Re-arm infinite scroll: a short result set may leave the sentinel
        // on screen, and the observer won't re-notify a continuously-visible
        // target. Self-stops once the Load More button hides (exhausted).
        this.#kickInfinite();
      }

      async #loadMore() {
        if (this.#busy) return;
        this.#page += 1;
        const seq = ++this.#reqSeq;
        this.#setBusy(true);
        let res;
        try {
          res = await this.#runQuery(this.#readState({ pageOverride: this.#page }));
        } catch (err) {
          console.error(`[${ELEMENT_NAME}] load-more failed`, err);
          this.#page -= 1;
          this.#setBusy(false);
          return;
        }
        if (seq !== this.#reqSeq) return; // a filter/sort change superseded this page
        const rawHits = (res && res.hits) || [];
        this.#renderCards(rawHits, { replace: false });
        this.#updateTotal(this.#displayTotal(res));
        this.#updateLoadMore(res, rawHits.length);
        this.#setBusy(false);
        // Infinite mode: if the sentinel is still on screen after appending
        // (e.g. a short batch on a tall viewport), re-observe to re-fire the
        // callback and pull the next page. Self-stops once the button hides.
        this.#kickInfinite();
      }

      // ── infinite scroll ───────────────────────────────────────────
      // Opt-in via the section's "Infinite scroll" setting (data-infinite).
      // We watch a zero-size sentinel below the grid; when it scrolls near the
      // viewport we fire the SAME #loadMore() the button uses. The button stays
      // in the DOM as the no-JS / keyboard fallback (CSS hides it in this mode),
      // and its `hidden` attribute remains the single source of "no more pages".
      #initInfiniteScroll() {
        if (this.dataset.infinite !== 'true') return;
        const sentinel = this.querySelector('[data-infinite-sentinel]');
        if (!sentinel || !('IntersectionObserver' in window)) return; // graceful fallback: button still works
        this.classList.add('dev-collection--infinite');
        this.#io = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting || this.#busy) continue;
            const btn = this.querySelector('[data-load-more]');
            if (btn && !btn.hasAttribute('hidden')) this.#loadMore(); // hidden === exhausted
          }
        }, { rootMargin: '600px 0px' }); // prefetch before the shopper hits the bottom
        this.#io.observe(sentinel);
      }

      // Re-arm the observer so a sentinel that's still on screen fires again
      // (IntersectionObserver won't re-notify a continuously-visible target).
      #kickInfinite() {
        if (!this.#io) return;
        const sentinel = this.querySelector('[data-infinite-sentinel]');
        if (!sentinel) return;
        this.#io.unobserve(sentinel);
        this.#io.observe(sentinel);
      }

      // ── rendering ─────────────────────────────────────────────────
      #renderCards(hits, { replace }) {
        const grid = this.querySelector('[data-results-grid]');
        if (!grid) return;
        if (replace) grid.innerHTML = '';
        if (!hits.length) {
          if (replace) this.#showEmpty(true);
          return;
        }
        this.#toggle('[data-empty]', false);
        const frag = marqo().buildCard(hits, this.#cardTemplate);
        this.#appendExtraSwatches(frag, hits);
        this.#hydrateLazyImages(frag);
        this.#carryFitmentToCards(frag);
        grid.appendChild(frag);
        this.#fitSwatches(grid);
        if (replace) this.#placeOffers(grid);
      }

      // Marqo's productCard defers swatch (and other secondary) images to
      // data-src / data-bg, expecting a lazy-loader to swap them in. This theme
      // ships none, so those imgs would keep src="" and render broken. Swap them
      // ourselves on every freshly-built fragment. (The main card image is set
      // via .src directly upstream, so it's unaffected.)
      // Carry the active vehicle (filter.fitments) onto every product-card link
      // so the PDP's "Shop My Ride" can pre-select it when the shopper clicks through.
      #carryFitmentToCards(root) {
        const fitKey = marqo().URL_PARAMS?.fitment || 'filter.fitments';
        const fit = new URLSearchParams(location.search).get(fitKey);
        if (!fit) return;
        root.querySelectorAll('.marqo-product-card a[href]').forEach((a) => {
          try {
            const u = new URL(a.getAttribute('href'), location.origin);
            u.searchParams.set(fitKey, fit);
            a.setAttribute('href', u.pathname + u.search + u.hash);
          } catch (_) { /* leave the link as-is */ }
        });
      }

      #hydrateLazyImages(root) {
        root.querySelectorAll('img[data-src]').forEach((img) => {
          if (img.dataset.src) { img.src = img.dataset.src; img.removeAttribute('data-src'); }
        });
        root.querySelectorAll('[data-bg]').forEach((el) => {
          if (el.dataset.bg) el.style.backgroundImage = `url("${el.dataset.bg}")`;
        });
      }

      // Marqo caps the swatch carousel at 5 colours (Nt=5 in marqo.js, app-wide).
      // To show the rest on the PLP without touching the shared bundle, mirror
      // its item builder (Ot) here: dedupe by colour exactly like Marqo (qt),
      // then append the swatches it didn't render. Keyed off the rendered count
      // so it self-adjusts if Marqo's cap ever changes. cards ↔ hits are 1:1 in
      // buildCard's output order.
      #appendExtraSwatches(root, hits) {
        const cards = root.querySelectorAll('.marqo-product-card');
        cards.forEach((card, i) => {
          const hit = hits[i];
          if (!hit) return;
          const all = this.#dedupeSwatches(hit.swatches);
          const track = card.querySelector('.dev-plp-card__swt-track');
          const tpl = track && track.querySelector('template');
          if (!track || !tpl) return; // single-variant card: Marqo removed the slider
          const rendered = track.querySelectorAll('.dev-plp-card__swt-item').length;
          if (all.length <= rendered) return; // nothing was capped
          const proto = tpl.content.firstElementChild;
          const name = track.querySelector('.dev-plp-card__swt-input')?.name || 'productSwatch';
          all.slice(rendered).forEach((sw, k) => {
            const item = proto.cloneNode(true);
            const id = `${name}-x${rendered + k}`;
            const url = this.#resizeImg(sw.img, 150); // match Ot()'s 150×150 swatch
            const input = item.querySelector('input');
            const label = item.querySelector('label');
            const img = item.querySelector('img');
            const holder = item.querySelector('.image-holder');
            if (input) { input.id = id; input.name = name; }
            if (label) label.setAttribute('for', id);
            if (img) { img.setAttribute('data-src', url); img.alt = sw.colour || ''; img.removeAttribute('srcset'); }
            if (holder) { holder.setAttribute('data-bg', url); holder.setAttribute('data-swt', url); }
            track.appendChild(item);
          });
        });
      }

      // Dedupe swatches by colour, preserving order — mirrors marqo.js qt().
      #dedupeSwatches(swatches) {
        if (!Array.isArray(swatches)) return [];
        const seen = new Set();
        return swatches.filter((s) => {
          const c = s && s.colour;
          if (seen.has(c)) return false;
          seen.add(c);
          return true;
        });
      }

      // Build a Shopify CDN URL at _SIZExSIZE — mirrors marqo.js resizeImageUrl (M):
      // strip any existing _WxH suffix, then append the requested square size.
      #resizeImg(url, size) {
        if (!url) return url;
        try {
          const u = new URL(url, location.origin);
          const dot = u.pathname.lastIndexOf('.');
          if (dot === -1) return url;
          const ext = u.pathname.slice(dot);
          const base = u.pathname.slice(0, dot).replace(/_\d+x\d+$/, '');
          u.pathname = `${base}_${size}x${size}${ext}`;
          return u.toString();
        } catch { return url; }
      }

      // Hide the swatch prev/next arrows on any card whose thumbnails already
      // fit the card row. Compare the track's natural content width against the
      // full wrapper width (not the arrow-shrunk container) so the decision is
      // "do the thumbnails fit the card?" independent of the arrows themselves.
      // Item widths are CSS-fixed, so this is stable before swatch images load.
      #fitSwatches(root) {
        (root || this).querySelectorAll('.dev-plp-card__swatches').forEach((wrap) => {
          const track = wrap.querySelector('.dev-plp-card__swt-track');
          if (!track) { wrap.classList.add('dev-plp-card__swatches--no-nav'); return; } // ≤1 swatch: slider removed
          if (!wrap.clientWidth) return; // wrapper hidden right now — re-checked on next render/resize
          const fits = track.scrollWidth <= wrap.clientWidth + 1; // +1 for sub-pixel rounding
          wrap.classList.toggle('dev-plp-card__swatches--no-nav', fits);
        });
      }

      // Swatch click → swap the card's main image to that variant. Marqo wires
      // the radios and stores each swatch image in data-swt, but ships no change
      // handler and no variant JS runs on the PLP — so do the swap ourselves.
      #onSwatchChange(input) {
        const card = input.closest('.marqo-product-card');
        const item = input.closest('.dev-plp-card__swt-item');
        if (!card || !item) return;
        const holder = item.querySelector('.image-holder');
        const swt = item.querySelector('.dev-plp-card__swt-img');
        const src = (holder && holder.dataset.swt) || (swt && (swt.currentSrc || swt.src));
        const main = card.querySelector('.card__image img');
        if (!src || !main) return;
        main.removeAttribute('srcset');
        main.src = this.#upsizeImg(src, 340); // swatch url is _150x150 — bump to card size
        main.setAttribute('data-src', main.src);
        if (swt && swt.alt) main.alt = swt.alt;
      }

      // Marqo's resizeImageUrl emits `..._150x150.ext?v=`; rewrite just the
      // dimension suffix to the requested size, leave the rest of the URL intact.
      #upsizeImg(url, size) {
        return String(url).replace(/_\d+x\d+(\.\w+)(?=$|[?#])/, `_${size}x${size}$1`);
      }

      // Interleave Special Offering banners into the grid at each block's
      // position (data-after). Clones from the hidden [data-offers] store so a
      // re-render (which clears the grid) can re-place them.
      #placeOffers(grid) {
        const store = this.querySelector('[data-offers]');
        if (!store) return;
        const offers = store.querySelectorAll('[data-offer]');
        if (!offers.length) return;
        const cards = grid.querySelectorAll('.marqo-product-card');
        if (!cards.length) return;
        offers.forEach((offer) => {
          const after = Math.max(1, Number(offer.dataset.after) || 8);
          const ref = cards[Math.min(after, cards.length) - 1];
          const clone = offer.cloneNode(true);
          clone.removeAttribute('data-offer');
          clone.removeAttribute('hidden');
          if (ref) ref.after(clone); else grid.appendChild(clone);
        });
      }

      // ── recommendation carousels (Carousel V2 blocks) ──────────────
      // Reuses the same card <template> + buildCard. Each [data-carousel]
      // declares its kind/limit; we fetch from window.Marqo.recommendations
      // and fall back to the collection (or wildcard) so a carousel never
      // renders empty. Fail-soft: hide the block on error/no results.
      async #initCarousels() {
        const carousels = this.querySelectorAll('[data-carousel]');
        for (const track of carousels) {
          try {
            const limit = Number(track.dataset.limit) || 12;
            let hits = await this.#fetchCarousel(track.dataset.kind || 'recent-view', limit);
            if (!hits.length) hits = await this.#carouselFallback(limit);
            if (!hits.length) { this.#hideCarousel(track); continue; }
            const cfrag = marqo().buildCard(hits, this.#cardTemplate);
            this.#appendExtraSwatches(cfrag, hits);
            this.#hydrateLazyImages(cfrag);
            track.appendChild(cfrag);
            this.#fitSwatches(track);
          } catch (err) {
            console.error(`[${ELEMENT_NAME}] carousel failed`, err);
            this.#hideCarousel(track);
          }
        }
      }

      async #fetchCarousel(kind, limit) {
        const recs = marqo().recommendations;
        if (!recs) return [];
        const attrs = PLP_ATTRIBUTES;
        if (kind === 'recent-view') {
          const res = await recs.fetchRecentlyViewed({ limit, excludeIds: [], attributesToRetrieve: attrs });
          return (res && res.hits) || [];
        }
        if (kind === 'for-you') {
          const userId = (window.theme && window.theme.customer && window.theme.customer.id) || this.dataset.userId;
          if (userId) {
            const res = await recs.forYou({ limit, userId, attributesToRetrieve: attrs });
            if (res && res.hits && res.hits.length) return res.hits;
          }
          return [];
        }
        // similar / complementary need product seeds — use recently-viewed parents.
        const parents = this.#recentParentIds();
        if (!parents.length || !recs.resolveSeedIds) return [];
        const seeds = await recs.resolveSeedIds(parents).catch(() => []);
        if (!seeds.length) return [];
        const res = kind === 'complementary'
          ? await recs.complementary({ limit, docIds: seeds, eventType: 'ClickEvent', attributesToRetrieve: attrs })
          : await recs.similar({ limit, documentIds: seeds, attributesToRetrieve: attrs });
        return (res && res.hits) || [];
      }

      async #carouselFallback(limit) {
        try {
          if (this.dataset.collectionName) {
            const res = await marqo().collections({ collectionName: this.dataset.collectionName, limit, attributesToRetrieve: PLP_ATTRIBUTES });
            return (res && res.hits) || [];
          }
          const res = await marqo().search({ q: '*', limit, attributesToRetrieve: PLP_ATTRIBUTES });
          return (res && res.hits) || [];
        } catch (_) { return []; }
      }

      #recentParentIds() {
        try { return JSON.parse(localStorage.getItem('marqoRecentlyViewedParents') || '[]'); }
        catch (_) { return []; }
      }

      #hideCarousel(track) {
        (track.closest('.dev-collection__carousel') || track).setAttribute('hidden', '');
      }

      // Sub-category pills — the DEEPEST drill-down level, sourced from Marqo.
      //
      // The Liquid renders pills from the mega-menu linklists for the levels the
      // menu contains (top category and its sub-categories). But that menu is only
      // ~3 levels deep, so a "leaf" collection there (e.g. "Tools & Shop") gets no
      // menu children. For those, we fall back to Marqo's Categories facet
      // (productMetafieldCustomLevel1Categories) — the sub-categories actually
      // present in THIS collection's products — and link each to its child
      // collection using the store's handle convention: `{root}-{category-handle}`
      // (root = the top-category handle passed down as data-subpill-root; e.g.
      // "dirt-bike" + "air-inflators" → /collections/dirt-bike-air-inflators).
      //
      // Skipped entirely when Liquid already filled the row from the menu
      // (data-subpill-menu="true") — the menu links are exact, Marqo is the fallback.
      #renderSubpills(facets) {
        const ul = this.querySelector('[data-subpills]');
        if (!ul || ul.dataset.subpillMenu === 'true') return; // menu already provided the links

        // The deepest drill-down level (below where the mega-menu linklists stop)
        // comes from Marqo's `productType` facet ("Department") — the specific
        // sub-categories of THIS collection's products (e.g. Tools & Shop →
        // "Air Inflators", "Bearing Tools", …). NOTE: `productMetafieldCustomLevel1Categories`
        // is the RIDE TYPE (Dirt Bike/ATV/…), NOT a category level — don't use it here.
        // Each pill links to that sub-collection via the store handle convention
        // `{root}-{type-handle}` (root = top-category handle from data-subpill-root;
        // e.g. "dirt-bike" + "air-inflators" → /collections/dirt-bike-air-inflators).
        const root = (ul.dataset.subpillRoot || '').trim();
        const currentHandle = (this.dataset.collectionName || '').trim();
        const M = marqo();
        const bucket = facets && facets['productType'];
        const toHandle = (M && M.toHandle) ? M.toHandle : (s) =>
          String(s || '').toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-');
        const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
          || (window.theme && window.theme.routes && window.theme.routes.root) || '/';

        const items = (bucket && typeof bucket === 'object' ? facetEntries(bucket) : [])
          .map(([value, info]) => [value, (info && (info.count ?? (info.value && info.value.count))) || 0])
          .filter(([value]) => value)
          .sort((a, b) => b[1] - a[1])
          .map(([value]) => ({ label: value, handle: `${root}-${toHandle(value)}` }))
          // Drop any pill that resolves to the collection we're already on — a
          // self-link is useless (e.g. a leaf whose only productType is itself).
          .filter((it) => it.handle !== currentHandle);

        if (!root || !items.length) { ul.innerHTML = ''; ul.hidden = true; return; }

        ul.innerHTML = items.map((it) => {
          const href = `${rootUrl.replace(/\/$/, '')}/collections/${it.handle}`;
          return (
            `<li class="dev-collection__subpill">` +
            `<a class="dev-collection__subpill-link" href="${escapeAttr(href)}">${escapeHtml(it.label)}</a></li>`
          );
        }).join('');
        ul.hidden = false;
        // TEMP: verify the generated child-collection URLs resolve to real collections.
        try { console.log('[subpills] productType pills', { root, current: currentHandle, items: items.map((i) => i.handle) }); } catch (_) {}
      }

      #renderFacets(facets) {
        const host = this.querySelector('[data-facet-groups]');
        if (!host || !facets) return;
        // Checking a box re-requeries, which rebuilds this whole panel from
        // scratch (fresh counts) — that would otherwise silently wipe out
        // whatever the shopper had typed into a facet's own search box, and
        // the rows it had hidden, forcing them to retype it to narrow down
        // and check a second value in the same long list. Save each field's
        // query now, restore it (and reapply the same filtering) after the
        // rebuild below.
        const prevQueries = {};
        host.querySelectorAll('[data-facet-search]').forEach((el) => {
          if (el.value) prevQueries[el.dataset.facetSearch] = el.value;
        });
        const M = marqo();
        const applied = new URLSearchParams(location.search);
        const all = Object.keys(M.DEFAULT_FACETS.fields).filter((f) => f !== 'price');
        const fields = [
          ...FACET_ORDER.filter((f) => all.includes(f)),
          ...all.filter((f) => !FACET_ORDER.includes(f)),
        ];

        host.innerHTML = fields.map((field) => {
          const bucket = facets[field];
          if (!bucket || typeof bucket !== 'object') return '';
          const selected = applied.getAll(FILTER_PREFIX + field);
          const rowsArr = facetEntries(bucket).map(([value, info]) => {
            const count = (info && (info.count ?? (info.value && info.value.count))) || 0;
            if (!value) return '';
            const checked = selected.includes(value) ? ' checked' : '';
            return (
              `<li class="dev-facet__row" data-facet-row-value="${escapeAttr(value.toLowerCase())}"><label class="dev-facet__label">` +
              `<input type="checkbox" class="dev-facet__checkbox" data-facet-field="${escapeAttr(field)}" value="${escapeAttr(value)}"${checked}>` +
              `<span class="dev-facet__value">${escapeHtml(value)}</span>` +
              `<span class="dev-facet__count">(${count})</span></label></li>`
            );
          }).filter(Boolean);
          if (!rowsArr.length) return '';
          const label = M.labelFor(field);
          // Only worth a search box once there's actually something to narrow
          // down — below this, scanning the full list is faster than typing.
          const searchHtml = rowsArr.length >= FACET_SEARCH_MIN
            ? `<div class="dev-facet__search">` +
                `<input type="text" class="dev-facet__search-input" data-facet-search="${escapeAttr(field)}" ` +
                `placeholder="${escapeAttr('Search ' + label)}" aria-label="${escapeAttr('Filter ' + label)}" autocomplete="off">` +
              `</div>`
            : '';
          return (
            `<details class="dev-facet" data-facet-field="${escapeAttr(field)}" open>` +
            `<summary class="dev-facet__summary"><span class="dev-facet__title">${escapeHtml(label)}</span></summary>` +
            searchHtml +
            `<ul class="dev-facet__list" data-facet-list>${rowsArr.join('')}</ul></details>`
          );
        }).join('');

        // Restore each field's query and re-fire the same delegated `input`
        // listener #bindEvents already wires up, rather than duplicating its
        // hide/show logic here.
        Object.entries(prevQueries).forEach(([field, query]) => {
          const input = host.querySelector(`[data-facet-search="${CSS.escape(field)}"]`);
          if (!input) return; // that field didn't come back in this result set
          input.value = query;
          input.dispatchEvent(new Event('input', { bubbles: true }));
        });
      }

      // Best-effort: read the price facet's upper bound so the max input can
      // hint the catalog ceiling. The Marqo price-range facet keys vary, so
      // we scan every numeric we can find and take the largest.
      #syncPriceBounds(priceFacet) {
        if (!priceFacet || typeof priceFacet !== 'object') return;
        const maxInput = this.querySelector('[data-price-max]');
        if (!maxInput) return;
        // Marqo price buckets are keyed like "-Inf:50.0" / "1000.0:Inf".
        // Take the largest finite UPPER bound; skip the open-ended top
        // bucket so its lower number doesn't masquerade as the catalog max.
        let max = 0;
        for (const key of Object.keys(priceFacet)) {
          const parts = String(key).split(':');
          const upper = parts.length > 1 ? parts[1] : parts[0];
          if (/inf/i.test(upper)) continue;
          const n = parseFloat(upper);
          if (Number.isFinite(n)) max = Math.max(max, n);
        }
        if (max > 0) {
          maxInput.setAttribute('data-max', String(Math.ceil(max)));
          if (!maxInput.placeholder) maxInput.placeholder = String(Math.ceil(max));
        }
      }

      #renderActiveChips() {
        const host = this.querySelector('[data-active-chips]');
        if (!host) return;
        const params = new URLSearchParams(location.search);
        const fitKey = marqo().URL_PARAMS?.fitment;
        const chips = [];
        for (const [key, value] of params.entries()) {
          if (!key.startsWith(FILTER_PREFIX) || !value) continue;
          let label = value;
          if (key === PRICE_LOW_KEY) label = `Min ${value}`;
          else if (key === PRICE_HIGH_KEY) label = `Max ${value}`;
          else if (key === fitKey) label = value.split('>').join(' '); // readable vehicle
          chips.push(
            `<button type="button" class="dev-collection__chip" data-chip-key="${escapeAttr(key)}" data-chip-value="${escapeAttr(value)}">` +
            `<span class="dev-collection__chip-text">${escapeHtml(label)}</span><span class="dev-collection__chip-x" aria-hidden="true">×</span></button>`
          );
        }
        host.innerHTML = chips.join('');
        this.#toggle('[data-clear-all]', chips.length > 0);
      }

      // Shop My Ride results view: the shopper arrived via a vehicle deep link
      // (filter.fitments=…, from Your Garage / Ride Selector), so this is a
      // ride-scoped result set, not a node in the category tree. Hide the category
      // breadcrumb and the sub-category pills — neither is meaningful here. Runs
      // after #renderSubpills (which may have shown the pills) so it wins; when the
      // vehicle chip is removed, active=false restores the breadcrumb and leaves the
      // pills to #renderSubpills' own show/hide decision.
      #applyRideResultsChrome() {
        let active = false;
        try {
          const v = marqo().handleizeYMM(location.search);
          active = !!(v && v.vehicleName);
        } catch (_) { active = false; }
        const crumb = this.querySelector('[data-breadcrumb]');
        if (crumb) crumb.hidden = active;
        // Only force-hide the pills; when inactive, leave them to #renderSubpills
        // (which hides the row anyway when there are no child collections).
        if (active) {
          const pills = this.querySelector('[data-subpills]');
          if (pills) pills.hidden = true;
        }
      }

      // Hero title reflects the active vehicle deep link (filter.fitments), e.g.
      // "Guaranteed To Fit Your Dirt Bike 2022 Kawasaki KX450"; falls back to the
      // collection's own title when no vehicle is selected. Reuses handleizeYMM so
      // the vehicle string matches the rest of the YMM flow.
      #applyFitmentHeading() {
        let vehicle = '';
        let vehicleType = '';
        try {
          const v = marqo().handleizeYMM(location.search);
          if (v && v.vehicleName) vehicle = v.vehicleName;
          const typePart = v && Array.isArray(v.parts) ? v.parts.find((p) => p.key === 'type') : null;
          if (typePart) vehicleType = typePart.value;
        } catch (_) { /* no Marqo / malformed — fall back to the collection title */ }
        if (this.#heroTitleEl) {
          this.#heroTitleEl.textContent = vehicle
            ? `${this.#fitmentPrefix} ${vehicle}`.trim()
            : this.#heroTitleDefault;
        }
        this.#applyFitmentCopy(vehicle);
        this.#applyFitmentBanner(vehicleType);
      }

      // Hero copy mirrors the title: a vehicle-specific description from the
      // fitment_hero_copy setting ([vehicle] token) when a ride is active, else the
      // collection's own copy. Creates the copy element if Liquid rendered none, and
      // removes it again on restore so an empty collection hero stays clean.
      #applyFitmentCopy(vehicle) {
        if (!this.#fitmentCopyTpl) return; // merchant opted out → leave collection copy as-is
        if (vehicle) {
          const copy = this.#fitmentCopyTpl.replace(/\[vehicle\]/gi, vehicle);
          if (!this.#heroCopyEl && this.#heroBoxEl) {
            this.#heroCopyEl = document.createElement('div');
            this.#heroCopyEl.className = 'dev-collection__hero-copy';
            this.#heroBoxEl.appendChild(this.#heroCopyEl);
            this.#heroCopyCreated = true;
          }
          if (this.#heroCopyEl) this.#heroCopyEl.textContent = copy;
        } else if (this.#heroCopyCreated && this.#heroCopyEl) {
          this.#heroCopyEl.remove();
          this.#heroCopyEl = null;
          this.#heroCopyCreated = false;
        } else if (this.#heroCopyEl && this.#heroCopyDefault != null) {
          this.#heroCopyEl.innerHTML = this.#heroCopyDefault;
        }
      }

      // Parses the Shop My Ride banner JSON island (sections/dev-collection.liquid)
      // into { [normalized vehicle type]: { desk, mob } }. Normalizes both sides
      // (trim + lowercase) the same way #applyFitmentBanner reads the URL's vehicle
      // type — `sbv_type` isn't enum-validated, so this absorbs trivial case/
      // whitespace drift without silently failing on it. null when the section
      // rendered no island at all (no banner entries configured).
      #parseSbvBanners() {
        const el = this.querySelector('script[type="application/json"][data-sbv-banners]');
        if (!el) return null;
        try {
          const raw = JSON.parse(el.textContent || '{}');
          const map = {};
          Object.keys(raw).forEach((key) => {
            const norm = String(key).trim().toLowerCase();
            if (norm) map[norm] = raw[key];
          });
          return map;
        } catch (_) {
          return null;
        }
      }

      // Shop My Ride banner swap: the hero image becomes the one configured for
      // the active vehicle's type (sections/dev-collection.liquid's data-sbv-banners
      // island), falling back to the Liquid-rendered default when no vehicle is
      // selected, its type has no matching entry, or no island was rendered at all
      // (no default hero image to swap, or no banner entries configured).
      #applyFitmentBanner(vehicleType) {
        if (!this.#heroImgEl || !this.#sbvBanners) return;
        const key = String(vehicleType || '').trim().toLowerCase();
        const match = key ? this.#sbvBanners[key] : null;

        if (match && match.desk) {
          this.#heroImgEl.src = match.desk;
          this.#heroImgEl.removeAttribute('srcset');
          if (match.mob) {
            if (!this.#heroSourceEl) {
              this.#heroSourceEl = document.createElement('source');
              this.#heroSourceEl.setAttribute('media', '(max-width: 768px)');
              this.#heroImgEl.parentElement.insertBefore(this.#heroSourceEl, this.#heroImgEl);
            }
            this.#heroSourceEl.srcset = match.mob;
          } else if (this.#heroSourceEl) {
            this.#heroSourceEl.srcset = '';
          }
        } else {
          this.#heroImgEl.src = this.#heroImgDefaultSrc;
          if (this.#heroImgDefaultSrcset) this.#heroImgEl.srcset = this.#heroImgDefaultSrcset;
          else this.#heroImgEl.removeAttribute('srcset');
          if (this.#heroSourceEl) this.#heroSourceEl.srcset = this.#heroSourceDefaultSrcset;
        }
      }

      // Marqo groups variants by parent product before computing totalHits
      // (per Marqo support's fix — see client.js), so it's the true product
      // total and safe to display directly. Only fall back to the loaded
      // card count if the response is missing totalHits entirely.
      #displayTotal(res) {
        const total = Number(res && res.totalHits);
        if (Number.isFinite(total)) return total;
        return this.#loadedCount();
      }

      #loadedCount() {
        return this.querySelector('[data-results-grid]')?.querySelectorAll('.marqo-product-card').length || 0;
      }

      // "Showing X of Y products" throughout — while more remain to load AND
      // once every product is on the page (e.g. "Showing 27 of 27 products"),
      // so the count stays in the same format start to finish. Falls back to a
      // plain total when the grid hasn't rendered anything yet (loaded === 0),
      // since "Showing 0 of Y" would be misleading before results are on the
      // page. Written to every [data-result-count] node — the toolbar one AND
      // the copy just above Load More (also RDI's pattern), so both stay in
      // sync off one call.
      #updateTotal(total) {
        const els = this.querySelectorAll('[data-result-count]');
        if (!els.length) return;
        const n = Number(total) || 0;
        const loaded = this.#loadedCount();
        const shown = Math.min(loaded, n);
        let text;
        if (loaded === 0) {
          text = n === 1 ? '1 product' : `${n} products`;
        } else {
          text = n === 1 ? `Showing ${shown} of 1 product` : `Showing ${shown} of ${n} products`;
        }
        els.forEach((el) => { el.textContent = text; });
      }

      #updateLoadMore(res, lastBatchCount) {
        const btn = this.querySelector('[data-load-more]');
        if (!btn) return;
        const limit = Number(this.dataset.limit) || 24;
        const loaded = this.#page * limit;               // end of the shown window, in PRODUCTS
        const exhausted = !!(res && res._serverExhausted);
        // NB: res.totalHits is VARIANT-level (see #displayTotal) and must not be
        // compared against a product count — doing so left this guard permanently
        // false, so the button never hid. The engine exposes _dedupedSoFar (unique
        // products discovered), which equals the true product total once the server
        // stream is exhausted; that's the correct signal to compare against.
        const dedupedSoFar = Number(res && res._dedupedSoFar);
        let done;
        if (lastBatchCount < limit) {
          done = true;                                   // couldn't fill this page → nothing beyond it
        } else if (exhausted) {
          // Whole catalogue seen: more pages only if products remain past the window.
          done = !Number.isFinite(dedupedSoFar) || dedupedSoFar <= loaded;
        } else {
          done = false;                                  // full page + server may hold more → keep the button
        }
        btn.toggleAttribute('hidden', done);
      }

      // ── url + control sync ────────────────────────────────────────
      #syncControlsFromUrl() {
        const params = new URLSearchParams(location.search);
        const sortSel = this.querySelector('[data-sort]');
        if (sortSel) {
          const wanted = params.get('sort') || '';
          const opt = [...sortSel.options].find((o) => o.value === wanted);
          sortSel.value = opt ? wanted : '';
        }
        const min = this.querySelector('[data-price-min]');
        const max = this.querySelector('[data-price-max]');
        if (min) min.value = params.get(PRICE_LOW_KEY) || '';
        if (max) max.value = params.get(PRICE_HIGH_KEY) || '';
      }

      #pushUrl(params) {
        params.delete('page');
        const qs = params.toString();
        history.pushState(null, '', qs ? '?' + qs : location.pathname);
      }

      // ── events ────────────────────────────────────────────────────
      #bindEvents() {
        // Delegated change: facet checkbox toggles (list is re-rendered each
        // query) and product-card swatch selection (cards are cloned at runtime).
        this.addEventListener('change', (e) => {
          const cb = e.target.closest('input[data-facet-field]');
          if (cb) { this.#onFilterChange(cb.dataset.facetField, cb.value, cb.checked); return; }
          const swt = e.target.closest('.dev-plp-card__swt-input');
          if (swt) this.#onSwatchChange(swt);
        });

        // Delegated input: per-facet search box narrows that group's own
        // option list only (hidden, not removed — unchecking nothing, just
        // visual). Re-bound implicitly each time since it's delegated on
        // `this`, same as the checkbox/swatch listener above — #renderFacets
        // rebuilds the facet markup from scratch on every query.
        this.addEventListener('input', (e) => {
          const search = e.target.closest('[data-facet-search]');
          if (!search) return;
          const list = search.closest('.dev-facet')?.querySelector('[data-facet-list]');
          if (!list) return;
          const query = search.value.trim().toLowerCase();
          list.querySelectorAll('[data-facet-row-value]').forEach((row) => {
            row.hidden = !(!query || row.dataset.facetRowValue.includes(query));
          });
        });

        // Sort dropdown.
        this.querySelector('[data-sort]')?.addEventListener('change', (e) => {
          const params = new URLSearchParams(location.search);
          const val = e.target.value;
          if (val) params.set('sort', val); else params.delete('sort');
          this.#pushUrl(params);
          this.#requery({ replace: true, resetPage: true });
        });

        // Price apply.
        this.querySelector('[data-price-apply]')?.addEventListener('click', () => this.#onPriceApply());

        // Active-filter chip remove + clear-all (delegated).
        this.querySelector('[data-active-chips]')?.addEventListener('click', (e) => {
          const chip = e.target.closest('[data-chip-key]');
          if (!chip) return;
          this.#onFilterChange(
            chip.dataset.chipKey.slice(FILTER_PREFIX.length),
            chip.dataset.chipValue,
            false,
            chip.dataset.chipKey, // pass raw key so filter.price.* removes correctly
          );
        });
        this.querySelector('[data-clear-all]')?.addEventListener('click', () => {
          const params = new URLSearchParams(location.search);
          [...params.keys()].forEach((k) => { if (k.startsWith(FILTER_PREFIX)) params.delete(k); });
          this.#pushUrl(params);
          this.#syncControlsFromUrl();
          this.#requery({ replace: true, resetPage: true });
        });

        // Load more.
        this.querySelector('[data-load-more]')?.addEventListener('click', () => this.#loadMore());

        // Back/forward replays the filter state (bound once; removed on disconnect).
        this.#onPopstate = () => {
          this.#syncControlsFromUrl();
          this.#requery({ replace: true, resetPage: true });
        };
        window.addEventListener('popstate', this.#onPopstate);

        // Re-check swatch-arrow fit when the card width changes (column count
        // shifts across breakpoints). Debounced — resize fires in bursts.
        let resizeTimer;
        this.#onResize = () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => this.#fitSwatches(this), 150);
        };
        window.addEventListener('resize', this.#onResize);

        // Mobile filter drawer: toggle button, close button, backdrop click.
        this.querySelector('[data-filter-toggle]')?.addEventListener('click', () => this.#toggleFilters(true));
        this.querySelector('[data-filter-close]')?.addEventListener('click', () => this.#toggleFilters(false));
        this.addEventListener('click', (e) => {
          if (!this.classList.contains('dev-collection--filters-open')) return;
          const rail = this.querySelector('[data-filter-rail]');
          if (rail && !rail.contains(e.target) && !e.target.closest('[data-filter-toggle]')) {
            this.#toggleFilters(false);
          }
        });
      }

      #onFilterChange(field, value, checked, rawKey) {
        const params = new URLSearchParams(location.search);
        const key = rawKey || (FILTER_PREFIX + field);
        if (checked) {
          params.append(key, value);
        } else {
          const kept = params.getAll(key).filter((v) => v !== value);
          params.delete(key);
          kept.forEach((v) => params.append(key, v));
        }
        this.#pushUrl(params);
        this.#requery({ replace: true, resetPage: true });
      }

      #onPriceApply() {
        const min = this.querySelector('[data-price-min]')?.value.trim();
        const max = this.querySelector('[data-price-max]')?.value.trim();
        const params = new URLSearchParams(location.search);
        if (min) params.set(PRICE_LOW_KEY, min); else params.delete(PRICE_LOW_KEY);
        if (max) params.set(PRICE_HIGH_KEY, max); else params.delete(PRICE_HIGH_KEY);
        this.#pushUrl(params);
        this.#requery({ replace: true, resetPage: true });
      }

      // ── ui state ──────────────────────────────────────────────────
      #setBusy(on) {
        this.#busy = on;
        this.#toggle('[data-loader]', !on);
        const grid = this.querySelector('[data-results-grid]');
        if (grid) grid.setAttribute('aria-busy', String(on));
        this.classList.toggle('dev-collection--loading', on);
      }

      #toggleFilters(open) {
        this.classList.toggle('dev-collection--filters-open', open);
        document.body.style.overflow = open ? 'hidden' : '';
      }

      #showEmpty(replace) {
        if (replace) {
          const grid = this.querySelector('[data-results-grid]');
          if (grid) grid.innerHTML = '';
        }
        this.#toggle('[data-empty]', true);
        this.#toggle('[data-load-more]', false);
        this.#updateTotal(0);
      }

      #fail() {
        this.classList.remove('is-ride-pending'); // never leave the hero/breadcrumb stuck invisible
        this.#toggle('[data-loader]', false);
        this.classList.add(`${ELEMENT_NAME}--empty`);
        // On a PLP we keep the section visible (facets + empty state stay
        // usable) unless the author explicitly opts into hiding.
        if (this.dataset.hideWhenEmpty === 'true') this.setAttribute('hidden', '');
      }

      // toggle a child's `hidden` attribute. show=true → visible.
      #toggle(selector, show) {
        const el = this.querySelector(selector);
        if (el) el.toggleAttribute('hidden', !show);
      }
    });
  }

  // ── tiny escapers (facet values come from Marqo, treat as untrusted) ──
  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  function escapeAttr(s) { return escapeHtml(s); }
})();
